/**
 * CR306: Theme Tokens CSS Resolver
 * 
 * Single source of truth for converting GlobalThemeTokens to CSS variables.
 * These variables are applied to the preview wrapper for isolated styling.
 */

import type { GlobalThemeTokens } from "./globalThemeTokens";
import {
  FONT_FAMILY_MAP,
  FONT_SCALE_MAP,
  LINE_HEIGHT_MAP,
  CARD_PADDING_MAP,
  SECTION_GAP_MAP,
  HEADER_HEIGHT_MAP,
  CARD_SHADOW_MAP,
  BADGE_HEIGHT_MAP,
  BADGE_RADIUS_MAP,
  BUTTON_RADIUS_MAP,
  STARBURST_SIZE_MAP,
  hslToCss,
} from "./globalThemeTokens";
import { resolveFontFamily } from "@/lib/theme/fontRegistry";

// ================================================================
// CSS VARIABLE NAMES (Constants for consistency)
// ================================================================

export const THEME_CSS_VARS = {
  // Colors
  cardBackground: "--wi-card-bg",
  cardBorder: "--wi-card-border",
  headerBackground: "--wi-header-bg",
  headerText: "--wi-header-text",
  bodyText: "--wi-body-text",
  mutedText: "--wi-muted-text",
  accentColor: "--wi-accent",
  
  // Typography
  headingFont: "--wi-heading-font",
  bodyFont: "--wi-body-font",
  fontScale: "--wi-font-scale",
  lineHeight: "--wi-line-height",
  
  // Spacing
  cardPadding: "--wi-card-padding",
  sectionGap: "--wi-section-gap",
  headerHeight: "--wi-header-height",
  
  // Card styling
  cardRadius: "--wi-card-radius",
  cardShadow: "--wi-card-shadow",
  cardBorderWidth: "--wi-card-border-width",
  
  // Ribbon styling
  ribbonRadius: "--wi-ribbon-radius",
  ribbonPadding: "--wi-ribbon-padding",
  ribbonShadow: "--wi-ribbon-shadow",
  
  // Badge styling
  badgeHeight: "--wi-badge-height",
  badgeRadius: "--wi-badge-radius",
  badgeBorderWidth: "--wi-badge-border-width",
  
  // Button styling
  buttonRadius: "--wi-button-radius",
  buttonSize: "--wi-button-size",
  
  // Starburst styling
  starburstWidth: "--wi-starburst-width",
  starburstHeight: "--wi-starburst-height",
  starburstOffsetX: "--wi-starburst-offset-x",
  starburstOffsetY: "--wi-starburst-offset-y",
} as const;

// ================================================================
// RESOLVER FUNCTION
// ================================================================

/**
 * Converts GlobalThemeTokens to a CSS variables object
 * for inline style application to preview wrappers.
 * 
 * @param tokens - The theme tokens to convert
 * @returns Record of CSS variable name -> value
 */
export function resolveGlobalThemeCssVars(
  tokens: GlobalThemeTokens
): Record<string, string> {
  const vars: Record<string, string> = {};
  
  // ================================
  // COLORS
  // ================================
  vars[THEME_CSS_VARS.cardBackground] = hslToCss(tokens.colors.cardBackground);
  vars[THEME_CSS_VARS.cardBorder] = hslToCss(tokens.colors.cardBorder);
  vars[THEME_CSS_VARS.headerBackground] = hslToCss(tokens.colors.headerBackground);
  vars[THEME_CSS_VARS.headerText] = hslToCss(tokens.colors.headerText);
  vars[THEME_CSS_VARS.bodyText] = hslToCss(tokens.colors.bodyText);
  vars[THEME_CSS_VARS.mutedText] = hslToCss(tokens.colors.mutedText);
  vars[THEME_CSS_VARS.accentColor] = hslToCss(tokens.colors.accentColor);
  
  // ================================
  // TYPOGRAPHY
  // ================================
  // Prefer font registry IDs over legacy font keys
  const headingFontFamily = tokens.typography.headingFontId
    ? resolveFontFamily(tokens.typography.headingFontId)
    : FONT_FAMILY_MAP[tokens.typography.headingFont];
  const bodyFontFamily = tokens.typography.bodyFontId
    ? resolveFontFamily(tokens.typography.bodyFontId)
    : FONT_FAMILY_MAP[tokens.typography.bodyFont];
  
  vars[THEME_CSS_VARS.headingFont] = headingFontFamily;
  vars[THEME_CSS_VARS.bodyFont] = bodyFontFamily;
  vars[THEME_CSS_VARS.fontScale] = String(FONT_SCALE_MAP[tokens.typography.fontScale]);
  vars[THEME_CSS_VARS.lineHeight] = String(LINE_HEIGHT_MAP[tokens.typography.lineHeight]);
  
  // ================================
  // SPACING
  // ================================
  vars[THEME_CSS_VARS.cardPadding] = CARD_PADDING_MAP[tokens.spacing.cardPadding];
  vars[THEME_CSS_VARS.sectionGap] = SECTION_GAP_MAP[tokens.spacing.sectionGap];
  vars[THEME_CSS_VARS.headerHeight] = HEADER_HEIGHT_MAP[tokens.spacing.headerHeight];
  
  // ================================
  // CARD STYLING
  // ================================
  vars[THEME_CSS_VARS.cardRadius] = `${tokens.components.cardCornerRadius}px`;
  vars[THEME_CSS_VARS.cardShadow] = CARD_SHADOW_MAP[tokens.components.cardShadow];
  vars[THEME_CSS_VARS.cardBorderWidth] = `${tokens.components.cardBorderWidth}px`;
  
  // ================================
  // RIBBON STYLING
  // ================================
  vars[THEME_CSS_VARS.ribbonRadius] = `${tokens.components.ribbon.cornerRadius}px`;
  vars[THEME_CSS_VARS.ribbonPadding] = tokens.components.ribbon.padding === "compact" 
    ? "0.25rem 0.5rem" 
    : tokens.components.ribbon.padding === "spacious" 
      ? "0.75rem 1.25rem" 
      : "0.5rem 0.75rem";
  vars[THEME_CSS_VARS.ribbonShadow] = tokens.components.ribbon.shadow === "none"
    ? "none"
    : tokens.components.ribbon.shadow === "medium"
      ? "0 4px 6px rgba(0,0,0,0.15)"
      : tokens.components.ribbon.shadow === "strong"
        ? "0 8px 16px rgba(0,0,0,0.25)"
        : "0 2px 4px rgba(0,0,0,0.08)";
  
  // ================================
  // BADGE STYLING
  // ================================
  vars[THEME_CSS_VARS.badgeHeight] = BADGE_HEIGHT_MAP[tokens.components.badge.height];
  vars[THEME_CSS_VARS.badgeRadius] = BADGE_RADIUS_MAP[tokens.components.badge.borderRadius];
  vars[THEME_CSS_VARS.badgeBorderWidth] = `${tokens.components.badge.borderWidth}px`;
  
  // ================================
  // BUTTON STYLING
  // ================================
  vars[THEME_CSS_VARS.buttonRadius] = BUTTON_RADIUS_MAP[tokens.components.button.borderRadius];
  vars[THEME_CSS_VARS.buttonSize] = tokens.components.button.size === "sm" 
    ? "0.75rem" 
    : tokens.components.button.size === "lg" 
      ? "1.25rem" 
      : "1rem";
  
  // ================================
  // STARBURST STYLING
  // ================================
  const starburstSize = STARBURST_SIZE_MAP[tokens.components.starburst.size];
  vars[THEME_CSS_VARS.starburstWidth] = starburstSize.width;
  vars[THEME_CSS_VARS.starburstHeight] = starburstSize.height;
  vars[THEME_CSS_VARS.starburstOffsetX] = `${tokens.components.starburst.offsetX}px`;
  vars[THEME_CSS_VARS.starburstOffsetY] = `${tokens.components.starburst.offsetY}px`;
  
  return vars;
}

/**
 * Converts CSS vars record to inline style object
 */
export function cssVarsToStyle(vars: Record<string, string>): React.CSSProperties {
  return vars as React.CSSProperties;
}

/**
 * Create a combined style object with theme vars
 */
export function createThemedPreviewStyle(
  tokens: GlobalThemeTokens,
  additionalStyles?: React.CSSProperties
): React.CSSProperties {
  const vars = resolveGlobalThemeCssVars(tokens);
  return {
    ...cssVarsToStyle(vars),
    ...additionalStyles,
  };
}
